import React, { useState, useEffect } from 'react';
import { Question } from '../../types/crime-game';
import { X, Check, XCircle, Languages, Eye } from 'lucide-react';

interface QuestionModalProps {
  isOpen: boolean;
  question: Question | null;
  onAnswer: (isCorrect: boolean, revealedAnswer: boolean) => void;
  onClose: () => void;
  onSkip?: () => void;
  onTranslate: (text: string) => Promise<string | null>;
  onRevealAnswer: () => void;
  translationPenalty: number;
  revealPenalty: number;
  questionTranslated?: boolean;
  answerRevealed?: boolean;
  canRevealNextTip?: boolean;
  onRevealNextTip?: () => void;
}

export const QuestionModal: React.FC<QuestionModalProps> = ({
  isOpen,
  question,
  onAnswer,
  onClose,
  onSkip,
  onTranslate,
  onRevealAnswer,
  translationPenalty,
  revealPenalty,
  questionTranslated = false,
  answerRevealed = false,
  canRevealNextTip = false,
  onRevealNextTip
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [translatedQuestion, setTranslatedQuestion] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && question) {
      setSelectedIndex(null);
      setHasSubmitted(false);
      setIsCorrect(false);
      setTranslatedQuestion(null);
    }
  }, [isOpen, question]);

  // React to answer reveal after confirmation
  useEffect(() => {
    if (answerRevealed && question && !hasSubmitted) {
      setSelectedIndex(question.correctIndex);
      setIsCorrect(true);
      setHasSubmitted(true);
      
      setTimeout(() => {
        onAnswer(false, true); // Revealed = not correct for scoring, but still unlocks
        setSelectedIndex(null);
        setHasSubmitted(false);
        setIsCorrect(false);
      }, 2000);
    }
  }, [answerRevealed, question, hasSubmitted, onAnswer]);

  if (!isOpen || !question) return null;

  const handleTranslate = async () => {
    if (translatedQuestion || questionTranslated) return; // Already translated
    const translation = await onTranslate(question.question);
    if (translation) {
      setTranslatedQuestion(translation);
    }
  };

  const handleReveal = () => {
    // onRevealAnswer will show confirmation modal
    // The actual reveal happens after confirmation in parent component
    onRevealAnswer();
    // Note: The UI update will happen when answerRevealed prop changes
  };

  const handleSelectOption = (index: number) => {
    if (hasSubmitted || answerRevealed) return;
    setSelectedIndex(index);
  };

  const handleSubmit = () => {
    if (selectedIndex === null || hasSubmitted) return;

    const correct = selectedIndex === question.correctIndex;
    setIsCorrect(correct);
    setHasSubmitted(true);

    // Auto-close after showing result
    setTimeout(() => {
      onAnswer(correct, false);
      setSelectedIndex(null);
      setHasSubmitted(false);
      setIsCorrect(false);
    }, 1500);
  };

  const handleSkip = () => {
    if (hasSubmitted || answerRevealed) return;
    
    // Mark as incorrect and not revealed
    setIsCorrect(false);
    setHasSubmitted(true);
    
    // Show feedback briefly, then skip
    setTimeout(() => {
      if (onSkip) {
        onSkip();
      } else {
        // Fallback: treat as incorrect answer
        onAnswer(false, false);
      }
      setSelectedIndex(null);
      setHasSubmitted(false);
      setIsCorrect(false);
    }, 500);
  };

  const displayQuestion = translatedQuestion || question.question;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {!hasSubmitted && !answerRevealed && (
          <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg text-sm text-blue-800">
            <strong>Stuck?</strong> You can skip this question, reveal the answer, or get a clue before answering!
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900">Case Question</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <p className="text-lg text-gray-700 flex-1">
              {displayQuestion}
            </p>
            {!questionTranslated && (
              <button
                onClick={handleTranslate}
                className="ml-4 flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title={`Translate to Portuguese (-${translationPenalty} points)`}
              >
                <Languages className="w-4 h-4" />
                <span className="text-xs">PT</span>
              </button>
            )}
          </div>

          {translatedQuestion && questionTranslated && (
            <p className="text-sm text-gray-500 italic mb-4">
              {question.question}
            </p>
          )}
          
          <div className="space-y-2 mb-4">
            {question.options.map((option, index) => {
              const isSelected = selectedIndex === index;
              const isCorrectOption = index === question.correctIndex;
              const showCorrect = hasSubmitted && (isCorrect || answerRevealed);
              
              let optionClass = 'w-full text-left p-4 rounded-lg transition-all border-2 ';
              
              if (hasSubmitted) {
                if (isCorrectOption) {
                  optionClass += 'border-green-500 bg-green-50 text-green-800';
                } else if (isSelected && !isCorrectOption) {
                  optionClass += 'border-red-500 bg-red-50 text-red-800';
                } else {
                  optionClass += 'border-gray-200 bg-gray-50 text-gray-600';
                }
              } else if (isSelected) {
                optionClass += 'border-indigo-500 bg-indigo-50 text-indigo-800';
              } else {
                optionClass += 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 text-gray-800';
              }

              return (
                <button
                  key={index}
                  onClick={() => handleSelectOption(index)}
                  disabled={hasSubmitted || answerRevealed}
                  className={optionClass}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {option}
                    </span>
                    {showCorrect && isCorrectOption && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                    {hasSubmitted && isSelected && !isCorrectOption && (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {hasSubmitted && (
            <div className={`mt-4 p-3 rounded-lg ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {isCorrect ? (
                <p className="font-medium">Correct!</p>
              ) : (
                <p className="font-medium">
                  Incorrect. The correct answer is: <span className="font-bold">{question.options[question.correctIndex]}</span>
                </p>
              )}
            </div>
          )}
        </div>

        {!hasSubmitted && !answerRevealed && (
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSkip}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Skip
            </button>
            <button
              onClick={handleReveal}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-orange-300 rounded-lg text-orange-700 hover:bg-orange-50 font-medium transition-colors"
              title={`Reveal answer (-${revealPenalty} points)`}
            >
              <Eye className="w-4 h-4" />
              Reveal Answer (-{revealPenalty}pts)
            </button>
            {canRevealNextTip && onRevealNextTip && (
              <button
                onClick={() => {
                  onRevealNextTip();
                  onClose();
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-amber-400 bg-amber-50 rounded-lg text-amber-700 hover:bg-amber-100 font-medium transition-colors"
                title={`Reveal clue (-${revealPenalty} points)`}
              >
                Reveal Clue
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={selectedIndex === null}
              className={`
                flex-1 px-4 py-2 rounded-lg text-white font-bold shadow-md transition-colors
                ${selectedIndex !== null
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-gray-300 cursor-not-allowed'
                }
              `}
            >
              Submit Answer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
